import { Braces, Webhook, Radar } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const CODE = `curl -X POST https://api.hlmcourier.com/v1/orders \\
  -H "Authorization: Bearer $HLM_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "pickup":   "Inova Fairfax Lab, Falls Church VA",
    "dropoff":  "Quest Reference Lab, Chantilly VA",
    "cargo":    "blood_pathology",
    "priority": "STAT",
    "window":   "<60min"
  }'

# 201 Created
{
  "order_id": "HLM-8F3K2Q",
  "status":   "driver_assigned",
  "eta_min":  38,
  "tracking": "wss://api.hlmcourier.com/v1/track/HLM-8F3K2Q"
}`;

const FEATURES = [
  { icon: Braces, title: "POST /v1/orders", desc: "Create instant STAT dispatches programmatically from your LIMS, EHR, or ERP." },
  { icon: Radar, title: "GET /v1/orders/{id}/tracking", desc: "Live coordinates, driver ETA, and milestone status for every shipment." },
  { icon: Webhook, title: "Webhooks", desc: "Driver Assigned, Specimen Picked Up, Delivered & Signed — pushed to your stack." },
];

export const ApiSection = () => (
  <section id="api" data-testid="api-section" className="bg-[#F8FAFC] py-24 lg:py-32 scroll-mt-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal>
          <Overline>API Integration — Chapter 06</Overline>
          <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            A developer-first courier API.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 max-w-lg">
            HLM provides a robust API that seamlessly integrates with your existing platforms —
            online ordering systems and ERP solutions — supporting automated order creation,
            real-time delivery tracking, and streamlined data exchange.
          </p>
        </Reveal>
        <div className="mt-10 space-y-6">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="flex items-start gap-4" data-testid={`api-feature-${i}`}>
                <span className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-[#0047FF]" />
                </span>
                <div>
                  <h3 className="font-mono text-sm font-bold text-slate-900">{f.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{f.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.15}>
        <div data-testid="api-code-block" className="relative rounded-[1.5rem] bg-[#0B1D4F] border border-blue-900/60 shadow-2xl shadow-blue-900/20 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
            <span className="h-3 w-3 rounded-full bg-red-400/80" />
            <span className="h-3 w-3 rounded-full bg-amber-400/80" />
            <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
            <span className="ml-3 font-mono text-[11px] text-blue-300/80 tracking-wider">dispatch.sh — HLM Courier API</span>
          </div>
          <pre className="p-6 overflow-x-auto text-[12.5px] leading-relaxed font-mono text-blue-100/90">{CODE}</pre>
        </div>
      </Reveal>
    </div>
  </section>
);
